import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type editAnswersMatrixElementCellDialogForm_AddWrongAnswerInMatrixElementCellMutation } from "@relay/editAnswersMatrixElementCellDialogForm_AddWrongAnswerInMatrixElementCellMutation.graphql";
import { type editAnswersMatrixElementCellDialogForm_SetCorrectAnswerInMatrixElementCellMutation } from "@relay/editAnswersMatrixElementCellDialogForm_SetCorrectAnswerInMatrixElementCellMutation.graphql";
import {
	ADD_WRONG_ANSWER_IN_MATRIX_ELEMENT_CELL_MUTATION,
	ANSWER_MATRIX_ELEMENT_CELL_FRAGMENT,
	SET_CORRECT_ANSWER_IN_MATRIX_ELEMENT_CELL_MUTATION,
} from "./edit-answers-matrix-element-cell-dialog-form.graphql";
import {
	type EditAnswersMatrixElementCellDialogFormFormState,
	type EditAnswersMatrixElementCellDialogFormProps,
} from "./edit-answers-matrix-element-cell-dialog-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { EditWrongAnswerInMatrixElementCellForm } from "../edit-wrong-answer-in-matrix-element-cell-form";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const EditAnswersMatrixElementCellDialogForm = ({
	answersMatrixElementCellFragmentRef,
	matrixElementId,
	onBack,
}: EditAnswersMatrixElementCellDialogFormProps) => {
	const answerCell = useFragment(
		ANSWER_MATRIX_ELEMENT_CELL_FRAGMENT,
		answersMatrixElementCellFragmentRef,
	);
	const [setCorrectAnswerInMatrixElementCell, isSettingCorrectAnswerInMatrixElementCell] =
		useMutation<editAnswersMatrixElementCellDialogForm_SetCorrectAnswerInMatrixElementCellMutation>(
			SET_CORRECT_ANSWER_IN_MATRIX_ELEMENT_CELL_MUTATION,
		);
	const [addWrongAnswerInMatrixElementCell] =
		useMutation<editAnswersMatrixElementCellDialogForm_AddWrongAnswerInMatrixElementCellMutation>(
			ADD_WRONG_ANSWER_IN_MATRIX_ELEMENT_CELL_MUTATION,
		);

	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const dispatch = useTypedDispatch();
	const formId = "MatrixElementForm";
	const formik = useFormik<EditAnswersMatrixElementCellDialogFormFormState>({
		initialValues: {
			correctAnswerContent: answerCell.correctAnswer.content,
			wrongAnswersContent: answerCell.wrongAnswers.map((a) => a.content),
		},
		enableReinitialize: true,
		onSubmit: (values) => {
			setCorrectAnswerInMatrixElementCell({
				variables: {
					input: {
						matrixElementId,
						newCorrectAnswerContent: values.correctAnswerContent,
						matrixCellId: answerCell.id,
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
				updater: setDraft,
			});
		},
	});
	return (
		<>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<EditAnswersMatrixElementCellDialogFormFormState, string>
					name={"correctAnswerContent"}
					label={t("forms.correctAnswer")}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					component={DefaultTextFieldComponent}
					formikConfig={formik}
				/>
				<Button
					disabled={
						Object.entries(formik.touched).length === 0 ||
						isSettingCorrectAnswerInMatrixElementCell
					}
					type="submit"
					label={t("educationalOffer.saveCorrectAnswer")}
					className="mb-4"
				/>
			</form>
			<Card>
				<Button
					type="button"
					className="mb-2"
					label={t("educationalOffer.addWrongAnswer")}
					onClick={() => {
						addWrongAnswerInMatrixElementCell({
							variables: {
								input: {
									matrixElementId,
									matrixCellId: answerCell.id,
									wrongAnswerContent: t("educationalOffer.newWrongAnswer"),
								},
							},
							updater: setDraft,
						});
					}}
				/>

				<div className="m-2">{t("educationalOffer.wrongAnswerOptions")}</div>
				<Card>
					{answerCell.wrongAnswers.map((answer) => {
						return (
							<EditWrongAnswerInMatrixElementCellForm
								key={answer.id}
								answerMatrixElementCellFragmentRef={answerCell}
								matrixElementId={matrixElementId}
								content={answer.content}
								answerId={answer.id}
							/>
						);
					})}
				</Card>
			</Card>

			<Button
				type="button"
				onClick={onBack}
				label={t("actions.back")}
				className="p-button-secondary mt-2"
			/>
		</>
	);
};
